/**
 * Zentrale Farb-Token-Definitionen für die gesamte Anwendung.
 * Alle Rohfarbwerte werden hier definiert – an keiner anderen Stelle sollten Hex-Codes stehen.
 *
 * Dies sind **primitive Tokens**: modusunabhängige Rohwerte.
 * Die Palette-Dateien (lightPalette.ts / darkPalette.ts) ordnen diese
 * je nach Theme-Modus semantischen Bedeutungen zu.
 *
 * Struktur:
 * - brand:       EDEKA-Unternehmensfarben (Blau, Gelb)
 * - blue:        Primärpalette für den hellen Modus
 * - yellow:      Primärpalette für den dunklen Modus
 * - indigo:      Sekundärpalette für den hellen Modus
 * - slate:       Sekundärpalette für den dunklen Modus
 * - neutral:     Schwarz, Weiß, Grau-Skala
 * - surface:     Hintergrund- / Oberflächenfarben
 * - status:      Semantische Rückmeldungsfarben (Erfolg, Warnung, Fehler, Info)
 * - overlay:     Transparente Überlagerungen & Schatten
 * - scrollbar:   Scrollbalken-spezifische Farben
 */

// ─── Markenfarben ────────────────────────────────────────────────────────────

export const brand = {
  edekaBlue: {
    50: '#e3f2fb',
    100: '#bcdef6',
    200: '#92c9f1',
    300: '#69b4ea',
    400: '#4ba5e7',
    500: '#3096e3',
    600: '#2988d6',
    700: '#2076c4',
    900: '#0d4993',
    main: '#1a65b2',
  },
  edekaYellow: {
    50: '#fffce6',
    100: '#fef7c2',
    200: '#fef199',
    300: '#fdec70',
    400: '#fbe750',
    600: '#fad22c',
    700: '#f8ba25',
    800: '#f6a21e',
    900: '#f17811',
    main: '#fce531',
  },
} as const;

// ─── Blau-Skala (Primärfarbe heller Modus) ──────────────────────────────────

export const blue = {
  light: '#5782EF',
  main: '#245EEB',
  dark: '#1348CD',
} as const;

// ─── Gelb-Skala (Primärfarbe dunkler Modus) ──────────────────────────────────

export const yellow = {
  light: '#FDEF87',
  main: '#FDEA5E',
  dark: '#FDE635',
} as const;

// ─── Indigo-Skala (Sekundärfarbe heller Modus) ───────────────────────────────

export const indigo = {
  light: '#EBEFFF',
  main: '#ADBDFF',
  dark: '#708DFF',
} as const;

// ─── Slate-Skala (Sekundärfarbe dunkler Modus) ───────────────────────────────

export const slate = {
  light: '#353F5A',
  main: '#212738',
  dark: '#171B27',
} as const;

// ─── Neutralfarben ──────────────────────────────────────────────────────────

export const neutral = {
  black: '#030A12',
  white: '#FFFFFF',
  grey50: '#F5F5F5',
  grey100: '#E0E0E0',
  grey800: '#2B2B2B',
  grey900: '#121212',
  greyAlpha33: '#d9d9d933',
  greyAlpha59: '#d9d9e659',
  greyHoverLight: '#d9d9e6',
  greyHoverDark: '#3A3A3A',
  navGrey: '#9f9f9f',
  dashedBorder: '#999999',
  disabledButton: '#E0E0E0DE',
} as const;

// ─── Oberflächen- / Hintergrundfarben ────────────────────────────────────────

export const surface = {
  light: '#F7F9F9',
  lightAlpha50: '#F7F9F980',
  lightGrey: '#fafafa',
  lightGreyHeader: '#efefef',
  dark: '#343638',
  darkAlpha50: '#34363880',
  darkElevated: '#1E1E1E',
  darkHeader: '#2A2A2A',
  topBanner: '#001a33',
} as const;

// ─── Statusfarben ───────────────────────────────────────────────────────────

export const status = {
  warning: {
    light: '#FFF3E0',
    main: '#FF9800',
    dark: '#F57C00',
  },
  error: {
    light: '#FFEBEE',
    main: '#D32F2F',
    dark: '#B71C1C',
  },
  success: {
    light: '#E8F5E9',
    main: '#4CAF50',
    dark: '#388E3C',
  },
  info: {
    light: '#E1F5FE',
    main: '#03A9F4',
    dark: '#01579B',
  },
} as const;

// ─── Überlagerungen & Schatten ──────────────────────────────────────────────

export const overlay = {
  black87: 'rgba(0, 0, 0, 0.87)',
  black70: 'rgba(0, 0, 0, 0.7)',
  black60: 'rgba(0, 0, 0, 0.6)',
  black50: 'rgba(0, 0, 0, 0.5)',
  black31: 'rgba(0, 0, 0, 0.31)',
  black24: 'rgba(0, 0, 0, 0.24)',
  black20: 'rgba(0, 0, 0, 0.2)',
  black15: 'rgba(0, 0, 0, 0.15)',
  black10: 'rgba(0, 0, 0, 0.1)',
  white70: 'rgba(255, 255, 255, 0.7)',
  white20: 'rgba(255, 255, 255, 0.2)',
  white10: 'rgba(255, 255, 255, 0.1)',
  loginGradient: 'linear-gradient(60deg, #000000, 15% , #001F40, 85%, #274472)',
  glowShadow: '0px 0px 80px 15px rgba(255, 255, 255, 0.1)',
  textShadow: '0px 0px 9px black',
  shadowSm: '0 4px 8px rgba(0, 0, 0, 0.1)',
  shadowMd: '0 4px 8px rgba(0, 0, 0, 0.2)',
  shadowLg: '0px 4px 10.6px 3px rgba(0, 0, 0, 0.2)',
  shadowSticky: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
  shadowOutline: '0px 0px 1px 0px rgba(0, 0, 0, 0.5)',
  shadowNone: 'inset 0 0 6px rgba(0, 0, 0, 0)',
} as const;

// ─── Scrollbalken-Farben ────────────────────────────────────────────────────

export const scrollbar = {
  thumb: '#6b6b6b',
  track: '#2b2b2b',
  hover: '#d3d3d3',
} as const;

// ─── Akzent- / Hervorhebungsfarben ──────────────────────────────────────────

export const accent = {
  hoverYellow: '#FBC823',
} as const;

// ─── Digi-Cute Pastell-Palette ───────────────────────────────────────────────

export const cute = {
  pink: '#F8C8DC',
  pinkLight: '#FDE8F0',
  pinkMid: '#F4A7C1',
  pinkDark: '#E87FAD',
  lavender: '#D8B4FE',
  lavenderLight: '#EDE0FF',
  lavenderDark: '#C084FC',
  mint: '#B5EAD7',
  mintLight: '#D4F5E9',
  mintDark: '#81C784',
  sky: '#BAE6FD',
  skyLight: '#E0F2FE',
  skyDark: '#4FC3F7',
  peach: '#FFDAB9',
  peachLight: '#FFF0E0',
  peachDark: '#FFB366',
  lemon: '#FFF9C4',
  blush: '#F48FB1',
  blushLight: '#FCE4EC',
  blushDark: '#E91E63',
  white: '#FFFBFE',
  whitePaper: 'rgba(255, 255, 255, 0.85)',
  whitePaperMid: 'rgba(255, 255, 255, 0.75)',
  card: '#FFFFFF',
  textPrimary: '#4A3347',
  textSecondary: '#7B6178',
  sidebarBg: '#F3E5F5',
  sidebarActive: '#E1BEE7',
  sidebarHover: '#F8BBD0',
  appBarBg: '#FCE4EC',
  border: '#F8BBD0',
  shadow: '0 4px 12px rgba(248, 200, 220, 0.4)',
  glowShadow: '0px 0px 60px 10px rgba(216, 180, 254, 0.25)',
  cardAlpha: 'rgba(255, 255, 255, 1)',
  cardAlpha70: 'rgba(255, 255, 255, 0.7)',
  cardAlpha80: 'rgba(255, 255, 255, 0.8)',
  lavenderAlpha60: 'rgba(237, 224, 255, 0.6)',
  pinkAlpha50: 'rgba(248, 200, 220, 0.5)',
  pinkAlpha40: 'rgba(253, 232, 240, 0.4)',
  pinkSolid: 'rgba(253, 232, 240, 1)',
  loginGradient:
    'linear-gradient(135deg, #FCE4EC 0%, #F3E5F5 40%, #E8EAF6 100%)',
  loginFormBox: 'rgba(255, 251, 254, 0.85)',
} as const;
